import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as resellerService from "@/shared/api/services/resellerService";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Download,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import StatusBadge from "@/shared/components/merchant/StatusBadge";
import { th, enUS } from "date-fns/locale";



export default function ResellerTransactions() {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTx, setSelectedTx] = useState(null);

  const resellerName = "Asia Gaming Network";

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["reseller-all-transactions"],
    queryFn: () => transactionService.getResellerTransactions(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["reseller-merchants-list"],
    queryFn: () => resellerService.getMyMerchants(),
  });

  const filteredTransactions = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && tx.status !== statusFilter) return false;
    if (typeFilter !== "all" && tx.type !== typeFilter) return false;
    if (merchantFilter !== "all" && tx.merchant_id !== merchantFilter) return false;
    if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
    if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
    return true;
  });

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setMerchantFilter("all");
    setDateRange({ from: null, to: null });
  };


  const hasFilters = search || statusFilter !== "all" || typeFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  return (
    <ResellerLayout currentPage="ResellerTransactions" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("transactions")}</h1>
            <p className="text-slate-500">{filteredTransactions.length} {t("transactions")}</p>
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {t("export")}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={merchantFilter} onValueChange={setMerchantFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("merchant")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="success">{t("success")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="failed">{t("failed")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="deposit">{t("deposit")}</SelectItem>
                <SelectItem value="withdrawal">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {dateRange.from ? format(dateRange.from, "MMM d") : t("dateRange")}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => setDateRange(range || { from: null, to: null })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} size="icon">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t("merchant")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t("loading")}</td></tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t("noTransactionsFound")}</td></tr>
                ) : (
                  filteredTransactions.slice(0, 50).map((tx, i) => {
                    return (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        onClick={() => setSelectedTx(tx)}
                        className="hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === "deposit" ? "bg-violet-100" : "bg-emerald-100"
                              }`}>
                              {tx.type === "deposit" ? (
                                <ArrowDownToLine className="h-4 w-4 text-violet-600" />
                              ) : (
                                <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-mono text-sm">{tx.transaction_id?.slice(0, 10)}...</p>
                              <p className="text-xs text-slate-400 sm:hidden">{tx.merchant_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell text-sm">{tx.merchant_code || t("nA")}</td>
                        <td className="px-4 py-3 text-right font-semibold">฿{tx.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={tx.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 hidden md:table-cell">
                          {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : t("nA")}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TransactionDetailPanel
          transaction={selectedTx}
          open={!!selectedTx}
          onClose={() => setSelectedTx(null)}
        />
      </div>
    </ResellerLayout>
  );
}